// Copyright ©️ 2026 eVolve MEP, LLC

import { useRef, useState, useLayoutEffect } from 'react';

/** Approximate width (px) reserved for the "+N" overflow badge. */
export const OVERFLOW_BADGE_WIDTH = 42;
/** Gap (px) between badges — must match the `gap` on the container for accurate measurement. */
export const BADGE_GAP = 6;

/**
 * Measures badge widths in the DOM to determine how many fit in the container,
 * reserving space for a "+N" overflow badge when not all items fit.
 */
export const useBadgeTruncation = (
  /** Total number of items to potentially display. */
  itemCount: number,
  /** CSS selector used to query the badge elements within the container. */
  badgeSelector: string,
  /** When false, skips measurement and treats all items as visible. */
  enabled = true,
) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState<number>(itemCount);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container || !enabled) return undefined;

    const recalculate = () => {
      const containerWidth = container.getBoundingClientRect().width;
      const badgeEls = Array.from(container.querySelectorAll<HTMLElement>(badgeSelector));

      let usedWidth = 0;
      let count = 0;

      for (let i = 0; i < itemCount; i++) {
        const badgeWidth = badgeEls[i]?.getBoundingClientRect().width ?? 0;
        const gap = i === 0 ? 0 : BADGE_GAP;
        const isLast = i === itemCount - 1;
        const widthNeeded = usedWidth + gap + badgeWidth + (isLast ? 0 : OVERFLOW_BADGE_WIDTH + BADGE_GAP);

        if (widthNeeded <= containerWidth) {
          usedWidth += gap + badgeWidth;
          count++;
        } else {
          break;
        }
      }

      setVisibleCount(count);
    };

    const observer = new ResizeObserver(recalculate);
    observer.observe(container);
    recalculate();

    return () => observer.disconnect();
  }, [itemCount, badgeSelector, enabled]);

  return {
    containerRef,
    visibleCount: enabled ? visibleCount : itemCount,
    remainder: enabled ? itemCount - visibleCount : 0,
  };
};
